import React, { useState, useMemo } from "react";
import {
  TrendUp,
  TrendDown,
  Coins,
  CreditCard,
  ChartLine,
  Target,
} from "@phosphor-icons/react";
import { useData } from "../context/DataContext";
import { Asset } from "../types";
import { formatCurrency } from "../utils/calculations";
import { getFontFamily } from "../utils/fontConfig";
import ReactApexChart from "react-apexcharts";

interface ProjectionScenario {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  color: string;
  chartColor: string;
  data: Array<{ month: number; balance: number }>;
}

const Projections: React.FC = () => {
  const { getDashboardData } = useData();
  const dashboardData = getDashboardData();
  const [selectedTimeframe, setSelectedTimeframe] = useState<string>("1y");
  const [activeScenario, setActiveScenario] = useState<string>("current");
  const [viewMode, setViewMode] = useState<"single" | "comparison">("single");

  const { currentBalance, monthlyIncome, monthlyExpenses, assets, debts } =
    dashboardData;

  // Calculate scenarios after destructuring
  const scenarios = (() => {
    const months =
      selectedTimeframe === "6m"
        ? 6
        : selectedTimeframe === "1y"
        ? 12
        : selectedTimeframe === "3y"
        ? 36
        : selectedTimeframe === "5y"
        ? 60
        : selectedTimeframe === "20y"
        ? 240
        : selectedTimeframe === "40y"
        ? 480
        : 60;

    const scenarios: ProjectionScenario[] = [];

    // Current scenario (baseline)
    const currentData = [];
    let balance = currentBalance;
    for (let month = 0; month <= months; month++) {
      currentData.push({
        month,
        balance,
      });
      balance += monthlyIncome - monthlyExpenses;
    }
    scenarios.push({
      id: "current",
      name: "Current Path",
      description: "Based on your current income and expenses",
      icon: <ChartLine className="w-5 h-5" weight="duotone" />,
      color: "text-blue-500",
      chartColor: "#3b82f6",
      data: currentData,
    });

    // Income increase scenario (10% raise)
    const incomeIncreaseData = [];
    balance = currentBalance;
    const increasedIncome = monthlyIncome * 1.1;
    for (let month = 0; month <= months; month++) {
      incomeIncreaseData.push({
        month,
        balance,
      });
      balance += increasedIncome - monthlyExpenses;
    }
    scenarios.push({
      id: "income-increase",
      name: "Income +10%",
      description: "What if you get a 10% raise?",
      icon: <TrendUp className="w-5 h-5" weight="duotone" />,
      color: "text-green-500",
      chartColor: "#10b981",
      data: incomeIncreaseData,
    });

    // Expense reduction scenario (20% cut)
    const expenseReductionData = [];
    balance = currentBalance;
    const reducedExpenses = monthlyExpenses * 0.8;
    for (let month = 0; month <= months; month++) {
      expenseReductionData.push({
        month,
        balance,
      });
      balance += monthlyIncome - reducedExpenses;
    }
    scenarios.push({
      id: "expense-reduction",
      name: "Expenses -20%",
      description: "What if you reduce expenses by 20%?",
      icon: <TrendDown className="w-5 h-5" weight="duotone" />,
      color: "text-orange-500",
      chartColor: "#f59e0b",
      data: expenseReductionData,
    });

    // Investment growth scenario
    const investmentAssets =
      assets.filter(
        (asset: Asset) =>
          asset.type === "investment" || asset.type === "retirement"
      ) || [];
    const totalInvestmentAssets = investmentAssets.reduce(
      (sum: number, asset: Asset) => sum + asset.balance,
      0
    );

    if (totalInvestmentAssets > 0) {
      const investmentGrowthData = [];
      balance = currentBalance;
      const monthlyInvestmentGrowth = (totalInvestmentAssets * 0.07) / 12; // 7% annual return

      for (let month = 0; month <= months; month++) {
        investmentGrowthData.push({
          month,
          balance,
        });
        balance += monthlyIncome - monthlyExpenses + monthlyInvestmentGrowth;
      }
      scenarios.push({
        id: "investment-growth",
        name: "Investment Growth",
        description: "With 7% annual investment returns",
        icon: <Coins className="w-5 h-5" weight="duotone" />,
        color: "text-yellow-500",
        chartColor: "#eab308",
        data: investmentGrowthData,
      });
    }

    // Debt payoff scenario
    const totalDebt =
      debts?.reduce((sum: number, debt: any) => sum + debt.balance, 0) || 0;
    if (totalDebt > 0) {
      const debtPayoffData = [];
      balance = currentBalance;
      const monthlyDebtPayment = totalDebt / 12; // Pay off in 1 year

      for (let month = 0; month <= months; month++) {
        debtPayoffData.push({
          month,
          balance,
        });

        if (month < 12) {
          // During debt payoff period
          balance += monthlyIncome - monthlyExpenses - monthlyDebtPayment;
        } else {
          // After debt is paid off
          balance += monthlyIncome - monthlyExpenses;
        }
      }
      scenarios.push({
        id: "debt-payoff",
        name: "Debt Payoff",
        description: "Pay off all debts in 1 year",
        icon: <CreditCard className="w-5 h-5" weight="duotone" />,
        color: "text-purple-500",
        chartColor: "#8b5cf6",
        data: debtPayoffData,
      });
    }

    return scenarios;
  })();
  const activeScenarioData = scenarios.find((s) => s.id === activeScenario);

  // Enhanced chart options with better styling
  const chartOptions = useMemo(() => {
    if (!activeScenarioData) {
      return {
        chart: {
          type: "line" as const,
          height: 320,
        },
      };
    }

    return {
      chart: {
        type: "line" as const,
        height: 320,
        fontFamily: getFontFamily(),
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350,
          },
        },
      },
      stroke: {
        width: [0, 3],
        curve: "smooth" as const,
        lineCap: "round" as const,
      },
      markers: {
        size: 0,
        hover: {
          size: 0,
        },
      },
      fill: {
        type: ["gradient", "solid"],
        gradient: {
          shade: "light",
          type: "vertical",
          shadeIntensity: 0.4,
          gradientToColors: [activeScenarioData.chartColor, "#ffffff"],
          inverseColors: false,
          opacityFrom: 0.8,
          opacityTo: 0.1,
          stops: [0, 100],
        },
      },
      colors: [activeScenarioData.chartColor, "#3b82f6"],
      dataLabels: {
        enabled: false,
      },
      grid: {
        borderColor: "var(--chart-grid-color, #e5e7eb)",
        strokeDashArray: 4,
        xaxis: {
          lines: {
            show: true,
            color: "var(--chart-grid-color, #e5e7eb)",
          },
        },
        yaxis: {
          lines: {
            show: true,
            color: "var(--chart-grid-color, #e5e7eb)",
          },
        },
      },
      xaxis: {
        type: "category" as const,
        categories: activeScenarioData.data.map((d) => d.month),
        labels: {
          formatter: (value: string) => {
            const val = parseInt(value);
            const month = activeScenarioData.data[val]?.month || val;
            const date = new Date();
            date.setMonth(date.getMonth() + month);
            return date.toLocaleDateString("en-US", {
              month: "2-digit",
              year: "2-digit",
            });
          },
          style: {
            colors: "var(--chart-text-color, #6b7280)",
            fontSize: "12px",
            fontFamily: getFontFamily(),
          },
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      yaxis: [
        {
          title: {
            text: "Balance ($)",
            style: {
              color: "var(--chart-text-color, #6b7280)",
              fontSize: "12px",
              fontFamily: getFontFamily(),
            },
          },
          labels: {
            formatter: (val: number) => formatCurrency(val),
            style: {
              colors: "var(--chart-text-color, #6b7280)",
              fontSize: "12px",
            },
          },
        },
      ],
      tooltip: {
        shared: true,
        intersect: false,
        theme: "light",
        style: {
          fontSize: "12px",
          fontFamily: getFontFamily(),
        },
        x: {
          show: true,
          formatter: (val: number) => {
            const month = activeScenarioData.data[val]?.month || val;
            const date = new Date();
            date.setMonth(date.getMonth() + month);
            return date.toLocaleDateString("en-US", {
              month: "long",
              year: "numeric",
            });
          },
        },
        y: [
          {
            formatter: (val: number) => formatCurrency(val),
          },
        ],
        custom: ({ series, seriesIndex, dataPointIndex, w }: any) => {
          const balance = series[0][dataPointIndex];
          const month =
            activeScenarioData.data[dataPointIndex]?.month || dataPointIndex;
          const date = new Date();
          date.setMonth(date.getMonth() + month);

          const safeBalance =
            balance !== undefined && balance !== null ? balance : 0;

          return `
          <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-lg">
            <div class="mb-2">
              <p class="text-gray-700 dark:text-gray-300 font-medium">${date.toLocaleDateString(
                "en-US",
                { month: "long", year: "numeric" }
              )}</p>
            </div>
            <div class="space-y-2">
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">Balance:</span>
                <span class="font-semibold text-gray-900 dark:text-white">${formatCurrency(
                  safeBalance
                )}</span>
              </div>
            </div>
              </div>
            </div>
          </div>
        `;
        },
      },
      legend: {
        show: true,
        position: "top" as const,
        horizontalAlign: "left" as const,
        fontSize: "14px",
        fontFamily: getFontFamily(),
      },

      responsive: [
        {
          breakpoint: 640,
          options: {
            chart: {
              height: 200,
            },
            legend: {
              position: "bottom" as const,
              fontSize: "12px",
            },
            xaxis: {
              labels: {
                fontSize: "10px",
              },
            },
            yaxis: [
              {
                labels: {
                  fontSize: "10px",
                },
              },
              {
                labels: {
                  fontSize: "10px",
                },
              },
            ],
          },
        },
      ],
    };
  }, [activeScenarioData]);

  const chartSeries = useMemo(() => {
    if (!activeScenarioData) return [];

    // Filter out invalid data points
    const validData = activeScenarioData.data.filter(
      (d) =>
        typeof d.balance === "number" &&
        !isNaN(d.balance) &&
        isFinite(d.balance)
    );

    if (validData.length === 0) return [];

    return [
      {
        name: "Balance",
        type: "area",
        data: validData.map((d) => d.balance),
      },
    ];
  }, [activeScenarioData, selectedTimeframe]);

  // Comparison chart options
  const comparisonChartOptions = useMemo(
    () => ({
      chart: {
        type: "line" as const,
        height: 320,
        fontFamily: getFontFamily(),
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 800,
        },
      },
      stroke: {
        width: 3,
        curve: "smooth" as const,
        lineCap: "round" as const,
      },
      markers: {
        size: 0,
        hover: {
          size: 0,
        },
      },
      colors: scenarios.map((s) => s.chartColor),
      dataLabels: {
        enabled: false,
      },
      grid: {
        borderColor: "var(--chart-grid-color, #e5e7eb)",
        strokeDashArray: 4,
      },
      xaxis: {
        type: "category" as const,
        categories: scenarios[0]?.data.map((d) => d.month) || [],
        labels: {
          formatter: (value: string) => {
            const val = parseInt(value);
            const date = new Date();
            date.setMonth(date.getMonth() + val);
            return date.toLocaleDateString("en-US", {
              month: "2-digit",
              year: "2-digit",
            });
          },
          style: {
            colors: "var(--chart-text-color, #6b7280)",
            fontSize: "12px",
            fontFamily: getFontFamily(),
          },
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      yaxis: {
        title: {
          text: "Balance ($)",
          style: {
            color: "var(--chart-text-color, #6b7280)",
            fontSize: "12px",
            fontFamily: getFontFamily(),
          },
        },
        labels: {
          formatter: (val: number) => formatCurrency(val),
          style: {
            colors: "var(--chart-text-color, #6b7280)",
            fontSize: "12px",
          },
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        theme: "light",
        style: {
          fontSize: "12px",
          fontFamily: getFontFamily(),
        },
        custom: ({ series, seriesIndex, dataPointIndex, w }: any) => {
          const month =
            scenarios[0]?.data[dataPointIndex]?.month || dataPointIndex;
          const date = new Date();
          date.setMonth(date.getMonth() + month);

          let tooltipContent = `
          <div class="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-lg">
            <p class="text-gray-700 dark:text-gray-300 font-medium mb-3">${date.toLocaleDateString(
              "en-US",
              { month: "long", year: "numeric" }
            )}</p>
            <div class="space-y-2">
        `;

          scenarios.forEach((scenario, index) => {
            const balance = series[index]?.[dataPointIndex] || 0;
            tooltipContent += `
            <div class="flex justify-between items-center">
              <div class="flex items-center gap-2">
                <div class="w-3 h-3 rounded-full" style="background-color: ${
                  scenario.chartColor
                }"></div>
                <span class="text-gray-600 dark:text-gray-400">${
                  scenario.name
                }:</span>
              </div>
              <span class="font-semibold text-gray-900 dark:text-white">${formatCurrency(
                balance
              )}</span>
            </div>
          `;
          });

          tooltipContent += `
            </div>
          </div>
        `;

          return tooltipContent;
        },
      },
      legend: {
        show: true,
        position: "top" as const,
        horizontalAlign: "left" as const,
        fontSize: "14px",
        fontFamily: getFontFamily(),
      },
      responsive: [
        {
          breakpoint: 640,
          options: {
            chart: {
              height: 200,
            },
            legend: {
              position: "bottom" as const,
              fontSize: "12px",
            },
            xaxis: {
              labels: {
                fontSize: "10px",
              },
            },
            yaxis: {
              labels: {
                fontSize: "10px",
              },
            },
          },
        },
      ],
    }),
    [scenarios, selectedTimeframe]
  );

  const comparisonChartSeries = useMemo(() => {
    return scenarios
      .map((scenario) => {
        // Filter out invalid data points
        const validData = scenario.data.filter(
          (d) =>
            typeof d.balance === "number" &&
            !isNaN(d.balance) &&
            isFinite(d.balance)
        );

        if (validData.length === 0) return null;

        return {
          name: scenario.name,
          data: validData.map((d) => d.balance),
          markers: {
            size: 0,
          },
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null); // Remove null entries with proper typing
  }, [scenarios, selectedTimeframe]);

  return (
    <div className="space-y-6">
      {/* Controls - Mobile Optimized */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* View Mode Toggle */}
          {/* View Mode Selector - Hidden on Mobile */}
          <div className="hidden md:flex flex-col gap-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              View:
            </span>
            <div className="grid grid-cols-2 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 gap-1">
              {[
                {
                  value: "single",
                  label: "Single",
                  icon: <Target className="w-4 h-4" />,
                },
                {
                  value: "comparison",
                  label: "Compare",
                  icon: <ChartLine className="w-4 h-4" />,
                },
              ].map((mode) => (
                <button
                  key={mode.value}
                  onClick={() =>
                    setViewMode(mode.value as "single" | "comparison")
                  }
                  className={`px-2 sm:px-3 py-2 sm:py-3 text-sm font-medium rounded-md transition-colors flex items-center justify-center gap-1 min-h-[44px] ${
                    viewMode === mode.value
                      ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700"
                  }`}>
                  {mode.icon}
                  <span className="hidden sm:inline">{mode.label}</span>
                  <span className="sm:hidden">
                    {mode.label === "Single" ? "S" : "C"}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* Timeframe Selector */}
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Timeframe:
            </span>
            <div className="grid grid-cols-6 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 gap-1">
              {[
                { value: "6m", label: "6M" },
                { value: "1y", label: "1Y" },
                { value: "3y", label: "3Y" },
                { value: "5y", label: "5Y" },
                { value: "20y", label: "20Y" },
                { value: "40y", label: "40Y" },
              ].map((timeframe) => (
                <button
                  key={timeframe.value}
                  onClick={() => setSelectedTimeframe(timeframe.value)}
                  className={`px-2 sm:px-3 py-2 sm:py-3 text-sm font-medium rounded-md transition-colors flex items-center justify-center min-h-[44px] ${
                    selectedTimeframe === timeframe.value
                      ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700"
                  }`}
                  aria-pressed={selectedTimeframe === timeframe.value}
                  aria-label={`${timeframe.label} timeframe`}>
                  {timeframe.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Scenario Cards - Mobile Optimized */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {scenarios.map((scenario) => (
          <div
            key={scenario.id}
            onClick={() => setActiveScenario(scenario.id)}
            className={`card cursor-pointer transition-all duration-200 hover:scale-105 ${
              activeScenario === scenario.id
                ? "ring-2 ring-primary-500 bg-primary-50 dark:bg-primary-900/20"
                : "hover:bg-gray-50 dark:hover:bg-gray-800"
            }`}
            role="button"
            tabIndex={0}
            aria-pressed={activeScenario === scenario.id}
            aria-label={`Select ${scenario.name} scenario`}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setActiveScenario(scenario.id);
              }
            }}>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div
                  className={`p-2 rounded-lg bg-gray-100 dark:bg-gray-800 flex-shrink-0 ${scenario.color}`}>
                  {scenario.icon}
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                    {scenario.name}
                  </h3>
                  <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">
                    {scenario.description}
                  </p>
                </div>
              </div>
            </div>

            {/* Scenario Summary - Mobile Optimized */}
            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-700 dark:text-gray-300 text-xs">
                    Final Balance
                  </span>
                  <p className="font-semibold text-gray-900 dark:text-white text-sm">
                    {formatCurrency(
                      scenario.data[scenario.data.length - 1]?.balance || 0
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Charts - Mobile Optimized */}
      {viewMode === "single" && activeScenarioData ? (
        <div className="card hidden sm:block">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-2">
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                {activeScenarioData.name}
              </h3>
              <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">
                {activeScenarioData.description}
              </p>
            </div>
          </div>

          <div className="h-64 sm:h-80">
            {/* Desktop Chart */}
            <div className="hidden lg:block">
              {(() => {
                try {
                  // Validate data before rendering
                  const validSeries = chartSeries.filter(
                    (series) =>
                      Array.isArray(series.data) &&
                      series.data.every(
                        (val) =>
                          typeof val === "number" &&
                          !isNaN(val) &&
                          isFinite(val)
                      )
                  );

                  if (validSeries.length === 0) {
                    return (
                      <div className="text-center text-gray-500 py-8">
                        <p>No valid data available for chart</p>
                      </div>
                    );
                  }

                  return (
                    <ReactApexChart
                      options={chartOptions}
                      series={validSeries}
                      type="line"
                      height={320}
                    />
                  );
                } catch (error) {
                  console.error("Error rendering Projections chart:", error);
                  return (
                    <div className="text-center text-gray-500 py-8">
                      <p>Error loading chart data</p>
                    </div>
                  );
                }
              })()}
            </div>

            {/* Mobile Data Table */}
            <div className="lg:hidden">
              <div className="space-y-3">
                {activeScenarioData.data.slice(0, 6).map((item, index) => {
                  const date = new Date();
                  date.setMonth(date.getMonth() + item.month);
                  return (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div
                          className="w-2 h-2 rounded-full"
                          style={{
                            backgroundColor: activeScenarioData.chartColor,
                          }}></div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            Month {item.month}
                          </p>
                          <p className="text-xs text-gray-600 dark:text-gray-400">
                            {date.toLocaleDateString("en-US", {
                              month: "short",
                              year: "2-digit",
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(item.balance)}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      ) : viewMode === "comparison" ? (
        <div className="card hidden sm:block">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-2">
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Scenario Comparison
              </h3>
              <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">
                Compare balance projections across all scenarios
              </p>
            </div>
          </div>

          <div className="h-64 sm:h-80">
            {/* Desktop Chart */}
            <div className="hidden lg:block">
              {(() => {
                try {
                  // Validate data before rendering
                  const validSeries = comparisonChartSeries.filter(
                    (series) =>
                      series &&
                      Array.isArray(series.data) &&
                      series.data.every(
                        (val) =>
                          typeof val === "number" &&
                          !isNaN(val) &&
                          isFinite(val)
                      )
                  );

                  if (validSeries.length === 0) {
                    return (
                      <div className="text-center text-gray-500 py-8">
                        <p>No valid data available for chart</p>
                      </div>
                    );
                  }

                  return (
                    <ReactApexChart
                      options={comparisonChartOptions}
                      series={validSeries}
                      type="line"
                      height={320}
                    />
                  );
                } catch (error) {
                  console.error(
                    "Error rendering Projections comparison chart:",
                    error
                  );
                  return (
                    <div className="text-center text-gray-500 py-8">
                      <p>Error loading chart data</p>
                    </div>
                  );
                }
              })()}
            </div>

            {/* Mobile Data Table */}
            <div className="lg:hidden">
              <div className="space-y-3">
                {scenarios[0]?.data.slice(0, 6).map((item, index) => {
                  const date = new Date();
                  date.setMonth(date.getMonth() + item.month);
                  return (
                    <div
                      key={index}
                      className="space-y-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          Month {item.month}
                        </p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {date.toLocaleDateString("en-US", {
                            month: "short",
                            year: "2-digit",
                          })}
                        </p>
                      </div>
                      <div className="space-y-1">
                        {scenarios.map((scenario) => {
                          const scenarioData = scenario.data[index];
                          if (!scenarioData) return null;
                          return (
                            <div
                              key={scenario.id}
                              className="flex items-center justify-between">
                              <div className="flex items-center space-x-2">
                                <div
                                  className="w-2 h-2 rounded-full"
                                  style={{
                                    backgroundColor: scenario.chartColor,
                                  }}></div>
                                <span className="text-xs text-gray-600 dark:text-gray-400">
                                  {scenario.name}
                                </span>
                              </div>
                              <span className="text-xs font-medium text-gray-900 dark:text-white">
                                {formatCurrency(scenarioData.balance)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Projections;
